import { snakeToTitle } from '@/utils/string';

/**
 * The backend names its archive flag `is_archived` (and `<relation>_is_archived`
 * on flattened join columns). Titlecasing that verbatim gives headers that read
 * as a question — "Is Archived" — where the user expects the state. Every
 * frontend surface that labels a column (table header, columns dialog, filter
 * builder column picker, inspector field rows) drops the `is_` so it reads
 * "Archived" / "Location Zone Archived".
 *
 * Deliberately narrow: only the archive flag is rewritten. Other `is_*` boolean
 * columns ("Is Online", "Is Active", …) keep their generated names.
 */
const ARCHIVED_FLAG_PATTERN = /(^|_)is_archived$/;

/**
 * Integer foreign keys the interface presents as the thing they point at. Their
 * filter offers the lookup's names and the table renders those names, so the user
 * never sees or types the id, and a header ending in "ID" describes a column they
 * cannot recognise.
 *
 * Deliberately narrow, like the archive rule above: only columns whose picker and
 * cell both show the lookup's name are listed. Other `*_id` columns keep their
 * generated names, because the id is what the user works with there.
 */
const LOOKUP_ID_COLUMN_LABELS: Record<string, string> = {
    bucket_type_id: 'Bucket Type',
    custom_playlist_schedule_type_id: 'Schedule Type',
};

/**
 * Fields renamed because their generated name points at the wrong record.
 *
 * A Location Zone Player is called "Player 2007" everywhere, by its own id. Its
 * `player_id` is the id of the hardware unit in the `players` table. Titled
 * "Player ID", the value 31 read as "Player 31", which is a different player.
 * The hardware player is called a "Unit", so the column is "Unit ID" (SAU-437).
 */
const RENAMED_COLUMN_LABELS: Record<string, string> = {
    player_id: 'Unit ID',
};

/** Human-facing header for a snake_case model field. */
export function columnHeaderLabel(fieldName: string): string {
    return (
        RENAMED_COLUMN_LABELS[fieldName] ??
        LOOKUP_ID_COLUMN_LABELS[fieldName] ??
        snakeToTitle(fieldName.replace(ARCHIVED_FLAG_PATTERN, '$1archived'))
    );
}
